import { motion } from 'framer-motion';
 
const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
    },
};
 
interface PolicySection {
    heading?: string;
    paragraphs?: string[];
    bullets?: string[];
    subSections?: { label: string; bullets: string[] }[];
}
 
const sections: PolicySection[] = [
    {
        paragraphs: [
            'JAPFA COMFEED INDIA PVT. LTD. ("JAPFA", "Company", "we", "our", or "us") is committed to protecting the privacy and security of information collected through the Contract Growing / Contract Farming ("CGF") application, including its mobile application and web portal (collectively referred to as the "Platform").',
            'This Privacy Policy describes how we collect, use, store, process, and protect information when Line Supervisors, Farmers, Managers, and other authorized users access or use the Platform for contract farming, poultry flock management, feed distribution, and related field operations.',
            'By using the Platform, you agree to the collection and use of information in accordance with this Privacy Policy.',
        ],
    },
    {
        heading: '2. Purpose of the Application',
        paragraphs: [
            'The Contract Growing / Contract Farming (CGF) application is designed to support field-level poultry farming operations including daily flock recording, mortality and weight tracking, feed and medicine consumption, feed transfer between farmers, goods receipt and delivery challan verification, farmer onboarding (KYC), shed readiness tracking, daily journey and visit planning, and integration with the Company\'s SAP system for stock, purchase order, and movement data.',
            'The application is intended solely for authorized business and operational purposes related to contract poultry farming, supply chain coordination, and field supervision, and interfaces with the Company\'s backend SAP and database systems to keep farm-level records synchronized with enterprise records.',
        ],
    },
    {
        heading: '3. Information We Collect',
        paragraphs: [
            'Depending on your role and usage of the Platform, we may collect the following information:',
        ],
        subSections: [
            {
                label: 'A. Personal Information',
                bullets: [
                    'Full Name',
                    'User ID and Password (Line Supervisor credentials issued via SAP)',
                    'Mobile Number',
                    'Email Address (for Branch Head / Manager login)',
                    'Role and Access Level (Line Supervisor, Farmer, Manager, Branch Head, HOD, Visitor)',
                    'Branch and Reporting Structure Details',
                ],
            },
            {
                label: 'B. Farmer and Flock Information',
                bullets: [
                    'Farmer Name, Mobile Number, and Aadhaar Number',
                    'Farmer Address, Area, and City',
                    'Flock Book Number and Farm Coordinates',
                    'Farmer Survey / KYC Details and Location Photos',
                    'Flock Book History and Batch Records',
                    'Shed Readiness Status and Supporting Photos',
                ],
            },
            {
                label: 'C. Daily Recording and Production Information',
                bullets: [
                    'Daily Mortality Count and Remarks',
                    'Body Weight and CV% (calculated from scale/AWB readings)',
                    'Mortality Photographs',
                    'Medicine Consumption Records',
                    'Feed Consumption Records and Photographs',
                    'Opening Balance and Previous Day Entry Data',
                    'Daily Target and Visit Remarks Against Assigned Farms',
                ],
            },
            {
                label: 'D. Logistics, Stock, and Transaction Information',
                bullets: [
                    'Feed Transfer Requests, Reference IDs, and OTP Verification Records',
                    'Goods Receipt Note (GRN) and Delivery Challan (DC) Details',
                    'Material Receipt Note (MRN) Records',
                    'Current Stock, Feed Availability, and Tentative Feed Requirement Data',
                    'Purchase Order and Movement Data Synced with SAP',
                ],
            },
            {
                label: 'E. Location and Journey Information',
                bullets: [
                    'Journey Start/End Date, Time, and Vehicle Number',
                    'Opening and Closing Kilometer Readings',
                    'Real-Time Latitude/Longitude During Journey Tracking',
                    'Proximity Verification (e.g., presence within range of a farm before entry is permitted)',
                    'Travel Distance and Route History',
                ],
            },
            {
                label: 'F. Device and Technical Information',
                bullets: [
                    'Device Model and Operating System',
                    'Application Version (checked against active version on server)',
                    'IP Address and API Base URL Connectivity Logs',
                    'Login Activity and Session Data',
                    'Connected Weighing Scale / AWB Device Data',
                    'System and Error Logs',
                ],
            },
        ],
    },
    {
        heading: '4. Location Information',
        paragraphs: [
            'The Platform collects location information from mobile devices during an active journey and when location-based features are used by Line Supervisors and Visitors.',
            'Location information may be used for:',
        ],
        bullets: [
            'Verifying that a Line Supervisor is within the required proximity of a farm before allowing farm-level data entry',
            'Recording farmer and farm coordinates for flock book and KYC purposes',
            'Tracking daily journey routes, travel distance, and visit history',
            'Operational planning, monitoring, and compliance reporting',
        ],
    },
    {
        heading: '5. Camera Access',
        paragraphs: [
            'The Platform may request access to the device camera to:',
        ],
        bullets: [
            'Capture mortality-related photographs during daily recording',
            'Capture feed consumption photographs',
            'Capture shed readiness photographs',
            'Capture location and identity photographs during farmer KYC/survey',
            'Support verification during Goods Receipt and Delivery Challan processing',
        ],
    },
    {
        heading: '6. Storage Access',
        paragraphs: ['The Platform may request access to device storage for:'],
        bullets: [
            'Uploading photographs and supporting evidence related to daily recording, KYC, and shed readiness',
            'Generating and temporarily storing excel files exchanged with the SAP system (Read/Read Copy/Read Temp and Write/Write Copy/Write Temp folders)',
            'Storing application-related files required for offline or field data entry',
        ],
    },
    {
        heading: '7. How We Use Information',
        paragraphs: [
            'Information collected through the Platform may be used to:',
        ],
        bullets: [
            'Record and monitor daily flock performance, mortality, and body weight (CV%)',
            'Track medicine and feed consumption against each farmer',
            'Process feed transfer requests and confirm them via OTP verification',
            'Process Goods Receipt (GRN), Delivery Challan (DC), and Material Receipt Note (MRN) entries',
            'Assign and track daily visit plans and targets for Line Supervisors',
            'Synchronize farm-level data with the Company\'s SAP system for stock, purchase order, and movement records',
            'Maintain farmer KYC and flock book records',
            'Generate reports and dashboards for Managers and Branch Heads',
            'Maintain system security and provide technical support',
            'Comply with applicable legal and regulatory requirements',
        ],
    },
    {
        heading: '8. Data Sharing and Disclosure',
        paragraphs: [
            'We do not sell personal information.',
            'Information may be shared only under the following circumstances:',
        ],
        subSections: [
            {
                label: 'Authorized Internal Users',
                bullets: [
                    'Information may be accessed by authorized Line Supervisors, Managers, Branch Heads, HODs, and administrative staff who require access for legitimate contract farming, logistics, and supervisory purposes.',
                ],
            },
            {
                label: 'Enterprise Systems',
                bullets: [
                    'Data is exchanged with the Company\'s SAP system through automated file-based synchronization (Read/Write directories) for stock, purchase order, and movement records.',
                ],
            },
            {
                label: 'Service Providers',
                bullets: [
                    'Cloud and server hosting',
                    'Technical support and application maintenance',
                    'Database and file storage',
                    'System monitoring',
                ],
            },
            {
                label: 'Legal Requirements',
                bullets: [
                    'Applicable laws',
                    'Government and regulatory authorities',
                    'Court orders',
                    'Law enforcement agencies',
                ],
            },
        ],
    },
    {
        heading: '9. Data Retention',
        paragraphs: ['We retain information only for as long as necessary to:'],
        bullets: [
            'Support ongoing contract farming and field operations',
            'Maintain flock book, KYC, and farmer records',
            'Fulfill legal and regulatory obligations',
            'Reconcile stock, feed transfer, and delivery records with SAP',
            'Resolve disputes or claims',
            'Enforce company policies',
        ],
    },
    {
        heading: '10. Data Security',
        paragraphs: [
            'We implement reasonable administrative, technical, and organizational safeguards to protect information against unauthorized access, disclosure, alteration, or destruction.',
            'Security measures may include:',
        ],
        bullets: [
            'Role-based login and access control (Line Supervisor, Manager, Branch Head, HOD, Visitor)',
            'Password-protected credentials issued and managed via SAP',
            'Proximity-based verification before allowing sensitive field data entry',
            'OTP-based verification for feed transfer confirmation',
            'Audit logs and activity monitoring',
            'Periodic security reviews',
        ],
    },
    {
        heading: '11. User Responsibilities',
        paragraphs: ['Users are responsible for:'],
        bullets: [
            'Maintaining confidentiality of login credentials',
            'Protecting authorized devices and connected scale/AWB equipment',
            'Submitting accurate and timely daily recording, feed, and delivery information',
            'Providing accurate information during farmer KYC and survey',
            'Reporting unauthorized access or suspected security incidents',
            'Using the Platform only for authorized contract farming and business activities',
        ],
    },
    {
        heading: "12. Children's Privacy",
        paragraphs: [
            'The Platform is intended solely for authorized business users, farmers, and field personnel, and is not directed toward individuals under the age of 18 years.',
            'We do not knowingly collect personal information from children.',
        ],
    },
    {
        heading: '13. Your Rights',
        paragraphs: ['Subject to applicable laws, users may request:'],
        bullets: [
            'Access to their personal information',
            'Correction of inaccurate information',
            'Deletion of information where legally permissible',
            'Restriction of processing under certain circumstances',
        ],
    },
    {
        heading: '14. Account Deletion Request',
        paragraphs: [
            'Users may request deletion of their account and associated personal information by contacting the CGF Support Team.',
            'Certain flock book, transaction, financial, or compliance records may be retained where required by applicable laws or legitimate business requirements.',
            'For account deletion requests, contact: buddyassist.jcipl@japfa.com',
        ],
    },
    {
        heading: '15. Changes to this Privacy Policy',
        paragraphs: [
            'We may update this Privacy Policy from time to time to reflect operational, legal, regulatory, or technological changes.',
            "The latest version will always be available through the Platform and on the Company's website.",
            'Continued use of the Platform after any updates constitutes acceptance of the revised Privacy Policy.',
        ],
    },
    {
        heading: '16. Contact Us',
        paragraphs: [
            'If you have questions regarding this Privacy Policy or the handling of your information, please contact:',
            'JAPFA COMFEED INDIA PVT. LTD.\nPlot No. 91, Sakore Nagar\nViman Nagar\nPune – 411014\nIndia',
            'Email: buddyassist.jcipl@japfa.com',
            'Website: https://www.japfaindia.com',
        ],
    },
];
 
const CGFpolicy = () => {
    return (
        <>
            <section className="flex justify-center bg-[#160700] py-[60px] ps-8 align-middle lg:ps-14 xl:py-[80px]">
                <motion.div
                    className="my-auto flex flex-col items-start justify-center sm:w-[60%] md:w-[60%]"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                >
                    <h1 className="text-[28px] font-bold leading-[120%] text-white lg:text-[40px] xl:text-[52px]">
                        Privacy Policy – Contract Growing / Farming (CGF)
                    </h1>
                </motion.div>
            </section>
 
            {/* Policy Body */}
            <section className="bg-white px-6 py-12 sm:px-10 md:px-16 lg:px-24 xl:px-32">
                <div className="">
                    {/* Introduction paragraph before numbered sections */}
                    <motion.div
                        className="mb-6"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                    >
                        <h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
                            1. Introduction
                        </h2>
                        {sections[0].paragraphs?.map((p, i) => (
                            <p
                                key={i}
                                className="mb-3 text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
                            >
                                {p}
                            </p>
                        ))}
                    </motion.div>
 
                    {/* Remaining sections */}
                    {sections.slice(1).map((section, sIdx) => (
                        <motion.div
                            key={sIdx}
                            className="mb-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.1 }}
                            variants={fadeUp}
                        >
                            {section.heading && (
                                <h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
                                    {section.heading}
                                </h2>
                            )}
 
                            {section.paragraphs?.map((p, i) => (
                                <p
                                    key={i}
                                    className="mb-3 whitespace-pre-line text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
                                >
                                    {p}
                                </p>
                            ))}
 
                            {section.bullets && section.bullets.length > 0 && (
                                <ul className="mb-3 ml-5 list-disc space-y-1">
                                    {section.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="text-[15px] leading-[1.75] text-[#1b1b1b]"
                                        >
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            )}
 
                            {section.subSections?.map((sub, subIdx) => (
                                <div key={subIdx} className="mb-4 ml-2">
                                    <p className="mb-1 text-[15px] font-semibold text-[#1b1b1b]">
                                        {sub.label}
                                    </p>
                                    <ul className="ml-5 list-disc space-y-1">
                                        {sub.bullets.map((b, i) => (
                                            <li
                                                key={i}
                                                className="text-[15px] leading-[1.75] text-[#1b1b1b]"
                                            >
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </motion.div>
                    ))}
                </div>
            </section>
        </>
    );
};
 
export default CGFpolicy;
